import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const t = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—');
const monthLabel = (s) => new Date(`${s}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const dayLabel = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * Daily report history. Admins see everyone's reports (filter by person, date range,
 * text); every other employee sees only their own (enforced by the API).
 */
const DailyReportArchiveModal = ({ employees, isAdmin = true, onClose }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeId, setEmployeeId] = useState('');
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState('');
  const [text, setText] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    setLoading(true);
    api.getDailyReports({ employeeId: isAdmin ? employeeId || undefined : undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined })
      .then(setReports)
      .catch(() => toast.error('Could not load daily reports.'))
      .finally(() => setLoading(false));
  }, [employeeId, dateFrom, dateTo, isAdmin]);

  const sortedEmployees = useMemo(() => [...(employees || [])].sort((a, b) => a.display_name.localeCompare(b.display_name)), [employees]);

  const shown = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => `${r.employee.display_name} ${r.done_today} ${r.plan_tomorrow}`.toLowerCase().includes(q));
  }, [reports, text]);

  const perPerson = useMemo(() => {
    const m = {};
    shown.forEach((r) => {
      const k = r.employee.mavis_user_id;
      m[k] = m[k] || { id: k, name: r.employee.display_name, n: 0 };
      m[k].n += 1;
    });
    return Object.values(m).sort((a, b) => b.n - a.n);
  }, [shown]);

  const groups = useMemo(() => {
    const g = [];
    shown.forEach((r) => {
      const key = r.report_date.slice(0, 7);
      if (!g.length || g[g.length - 1].key !== key) g.push({ key, items: [] });
      g[g.length - 1].items.push(r);
    });
    return g;
  }, [shown]);

  const preset = (from) => { setDateFrom(from); setDateTo(''); };
  const preview = (s) => { const t = (s || '').replace(/\s+/g, ' ').trim(); return t.length > 130 ? `${t.slice(0, 130)}…` : t; };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 900, width: '94vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>{isAdmin ? 'Daily report history' : 'My daily report history'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          {[['Last 7 days', daysAgo(7)], ['Last 30 days', daysAgo(30)], ['Last 90 days', daysAgo(90)], ['All time', '']].map(([label, from]) => (
            <button key={label} type="button" className={dateFrom === from && !dateTo ? 'btn-primary' : 'btn-secondary'}
              style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={() => preset(from)}>{label}</button>
          ))}
          <input type="date" className="form-input" style={{ maxWidth: 150 }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From date" aria-label="From date" />
          <input type="date" className="form-input" style={{ maxWidth: 150 }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To date" aria-label="To date" />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          {isAdmin && (
            <select className="form-select" style={{ maxWidth: 220 }} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} aria-label="Employee">
              <option value="">All employees</option>
              {sortedEmployees.map((emp) => <option key={emp.mavis_user_id} value={emp.mavis_user_id}>{emp.display_name}</option>)}
            </select>
          )}
          <input type="search" className="form-input" style={{ flex: '1 1 220px' }} placeholder="Search text in reports…" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search reports" />
        </div>

        {isAdmin && !employeeId && perPerson.length > 0 && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem', fontSize: '0.8rem' }}>
            <span style={{ color: '#aab7c4', alignSelf: 'center' }}>Reports in range:</span>
            {perPerson.map((p) => (
              <button key={p.id} type="button" className="btn-secondary" style={{ padding: '2px 9px', fontSize: '0.8rem' }} onClick={() => setEmployeeId(String(p.id))}>
                {p.name} · {p.n}
              </button>
            ))}
          </div>
        )}

        <div style={{ color: '#aab7c4', fontSize: '0.8rem', marginBottom: '0.4rem' }}>{loading ? 'Loading…' : `${shown.length} report${shown.length === 1 ? '' : 's'}`}</div>

        {!loading && shown.length === 0 ? (
          <p style={{ color: '#aab7c4' }}>No reports match these filters.</p>
        ) : (
          <div style={{ maxHeight: '58vh', overflowY: 'auto' }}>
            {groups.map((g) => (
              <div key={g.key}>
                <div className="section-title" style={{ margin: '0.6rem 0 0.3rem' }}>{monthLabel(g.key)}</div>
                {g.items.map((r) => (
                  <div key={r.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.55rem 0.75rem', marginBottom: '0.4rem' }}>
                    <div role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                      onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => ({ ...o, [r.id]: !o[r.id] })); } }}
                      aria-expanded={!!open[r.id]}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <strong>{dayLabel(r.report_date)}{isAdmin ? ` — ${r.employee.display_name}` : ''}</strong>
                        <span style={{ fontSize: '0.75rem', color: '#aab7c4' }}>{open[r.id] ? 'Hide' : 'Show'}</span>
                      </div>
                      {r.work && (
                        <div style={{ fontSize: '0.8rem', marginTop: 2 }}>
                          {t(r.work.started_at)} → {t(r.work.finished_at)}{r.work.duration ? ` · ${r.work.duration}` : ''}
                          {(r.work.start_outside_office || r.work.finish_outside_office) && (
                            <span style={{ marginLeft: 8, color: '#ffcf7a', fontWeight: 700 }}>OUTSIDE OFFICE</span>
                          )}
                        </div>
                      )}
                      {!open[r.id] && <div style={{ fontSize: '0.82rem', color: '#aab7c4', marginTop: 2 }}>{preview(r.done_today)}</div>}
                    </div>
                    {open[r.id] && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        <div className="section-title" style={{ marginTop: 0 }}>Done today</div>
                        <p style={{ whiteSpace: 'pre-wrap', margin: '0 0 0.5rem' }}>{r.done_today}</p>
                        <div className="section-title">Plan for tomorrow</div>
                        <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{r.plan_tomorrow}</p>
                        <p style={{ fontSize: '0.72rem', color: '#aab7c4', marginTop: '0.5rem' }}>Submitted {new Date(r.submitted_at).toLocaleString()}</p>
                        {r.work && (
                          <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: 0 }}>
                            Started {t(r.work.started_at)} from {r.work.start_ip || 'unknown'}{r.work.start_outside_office ? ' (outside office)' : ''}
                            {' · '}Finished {t(r.work.finished_at)} from {r.work.finish_ip || 'unknown'}{r.work.finish_outside_office ? ' (outside office)' : ''}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyReportArchiveModal;
